import { useTranslation } from "react-i18next";
import { Switch } from "@/components/primitives/switch";
import { useMediaAutoDownloadState } from "@/zustand/MediaAutoDownloadState";
import { AUTO_DOWNLOAD_MAX_BYTES, MB } from "@/config/constants";

/**
 * Media auto-download preferences: per-type toggles, all default OFF (opt-in).
 * When on, full media/files under the per-type size cap are fetched into the
 * cache as messages render, so opening them is instant. The caps are fixed in
 * config (no user selector); each toggle shows its cap for transparency.
 */
export default function MediaAutoDownloadSettings() {
  const { t } = useTranslation();
  const autoDownloadPhotos = useMediaAutoDownloadState(
    (s) => s.autoDownloadPhotos,
  );
  const autoDownloadVideos = useMediaAutoDownloadState(
    (s) => s.autoDownloadVideos,
  );
  const autoDownloadFiles = useMediaAutoDownloadState(
    (s) => s.autoDownloadFiles,
  );
  const setAutoDownloadPhotos = useMediaAutoDownloadState(
    (s) => s.setAutoDownloadPhotos,
  );
  const setAutoDownloadVideos = useMediaAutoDownloadState(
    (s) => s.setAutoDownloadVideos,
  );
  const setAutoDownloadFiles = useMediaAutoDownloadState(
    (s) => s.setAutoDownloadFiles,
  );

  const toggles = [
    {
      id: "photos",
      label: t("autoDownloadPhotos", "Auto-download photos"),
      capMb: AUTO_DOWNLOAD_MAX_BYTES.image / MB,
      checked: autoDownloadPhotos,
      onChange: setAutoDownloadPhotos,
    },
    {
      id: "videos",
      label: t("autoDownloadVideos", "Auto-download videos"),
      capMb: AUTO_DOWNLOAD_MAX_BYTES.video / MB,
      checked: autoDownloadVideos,
      onChange: setAutoDownloadVideos,
    },
    {
      id: "files",
      label: t("autoDownloadFiles", "Auto-download files"),
      capMb: AUTO_DOWNLOAD_MAX_BYTES.file / MB,
      checked: autoDownloadFiles,
      onChange: setAutoDownloadFiles,
    },
  ];

  return (
    <div className="flex flex-col">
      <span className="py-1 text-sm text-muted-2">
        {t("mediaAutoDownload", "Media auto-download")}
      </span>

      {toggles.map(({ id, label, capMb, checked, onChange }) => (
        <div key={id} className="flex items-center justify-between py-2">
          <div className="flex flex-col">
            <span>{label}</span>
            <span className="text-xs text-muted-2">
              {t("autoDownloadCap", {
                mb: capMb,
                defaultValue: "Up to {mb} MB",
              })}
            </span>
          </div>
          <Switch
            checked={checked}
            onCheckedChange={onChange}
            aria-label={label}
          />
        </div>
      ))}
    </div>
  );
}
